import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';

const CategoryCard = ({ 
  category, 
  editingImageCategoryId, 
  imageUrlDraft, 
  savingImage,
  onEditImage,
  onCancelEditImage,
  onSaveImage,
  onSetImageUrlDraft,
  onDeleteCategory,
  getScoreColor
}) => {
  const { t } = useTranslation();
  return (
    <div className="bg-white rounded-xl mobile:rounded-2xl tablet:rounded-2xl laptop:rounded-2xl desktop:rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-all duration-300 transform hover:scale-[1.02] group">
      {/* Category Image */}
      <div className="relative h-32 mobile:h-40 tablet:h-48 laptop:h-48 desktop:h-48 bg-gradient-to-br from-gray-100 to-gray-200 overflow-hidden">
        {category.image ? (
          <img
            src={category.image.replace(/\\/g, "/")}
            alt={category.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            onError={(e) => {
              e.target.style.display = "none";
              e.target.nextSibling.style.display = "flex";
            }}
          />
        ) : null}
        <div
          className={`absolute inset-0 bg-gradient-to-br from-secondary-green/20 to-secondary-green/30 flex items-center justify-center pointer-events-none ${
            category.image ? "hidden" : "flex"
          }`}
        >
          <img src="/assets/icons/svg/default-image-placeholder.svg" alt="Category" className="w-12 h-12 mobile:w-16 mobile:h-16 tablet:w-16 tablet:h-16 laptop:w-16 laptop:h-16 desktop:w-16 desktop:h-16 text-secondary-green/60" />
        </div>

        {/* Image Edit Overlay */}
        {editingImageCategoryId === category.id && (
          <div className="absolute inset-0 z-10 bg-white/95 backdrop-blur-sm flex flex-col justify-center p-4">
            <label className="text-xs font-semibold text-gray-700 mb-2">Ny bild URL</label>
            <input
              type="text"
              value={imageUrlDraft}
              onChange={(e) => onSetImageUrlDraft(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary-green focus:border-transparent text-sm bg-white"
              placeholder="https://..."
            />
            <div className="mt-3 flex items-center justify-end space-x-2">
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); onCancelEditImage(); }}
                className="px-3 py-2 text-sm rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-gray-700"
                disabled={savingImage}
              >
                {t('Avbryt')}
              </button>
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); onSaveImage(category); }}
                className="px-3 py-2 text-sm rounded-lg bg-secondary-green text-white hover:opacity-90 disabled:opacity-50"
                disabled={savingImage || !imageUrlDraft}
              >
                {savingImage ? 'Sparar...' : 'Spara'}
              </button>
            </div>
          </div>
        )}

        {/* Edit and Delete Buttons */}
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onEditImage(category); }}
          className="absolute bottom-2 right-11 z-10 inline-flex items-center justify-center w-7 h-7 rounded-full bg-white/80 hover:bg-white/90 border border-gray-200 shadow-sm transition-colors"
          title="Redigera bild"
        >
          <img 
            src="/assets/icons/Redigera.png" 
            alt="Edit" 
            className="w-4 h-4 text-gray-600"
          />
        </button>
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDeleteCategory(category); }}
          className="absolute bottom-2 right-2 z-10 inline-flex items-center justify-center w-7 h-7 rounded-full bg-white/80 hover:bg-white/90 border border-gray-200 shadow-sm transition-colors"
          title="Radera kategori"
        >
          <img 
            src="/assets/icons/Radera.png" 
            alt="Delete" 
            className="w-4 h-4 text-gray-600"
          />
        </button>
        
        {/* Category Name Overlay */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent p-3 mobile:p-4 tablet:p-4 laptop:p-4 desktop:p-4 pointer-events-none">
          <h3 className="text-sm mobile:text-base tablet:text-lg laptop:text-lg desktop:text-lg font-bold text-white truncate">
            {t(category.name)}
          </h3>
        </div>
        
        {/* Sustainability Score Badge */}
        {/* <div className="absolute top-2 mobile:top-3 tablet:top-3 laptop:top-3 desktop:top-3 right-2 mobile:right-3 tablet:right-3 laptop:right-3 desktop:right-3 pointer-events-none">
          <div className={`px-2 mobile:px-3 tablet:px-3 laptop:px-3 desktop:px-3 py-1 mobile:py-1 tablet:py-1 laptop:py-1 desktop:py-1 rounded-full text-xs mobile:text-xs tablet:text-xs laptop:text-xs desktop:text-xs font-semibold text-white ${getScoreColor(category.avgSustainability)}`}>
            {category.avgSustainability}%
          </div>
        </div> */}
      </div>
      
      {/* Category Info */}
      <div className="p-3 mobile:p-4 tablet:p-4 laptop:p-4 desktop:p-4">
        <div className="flex items-center justify-between mb-2 mobile:mb-3 tablet:mb-3 laptop:mb-3 desktop:mb-3">
          <span className="text-xs mobile:text-sm tablet:text-sm laptop:text-sm desktop:text-sm text-gray-500 lowercase">
            {category.dishCount || 0} {t('Recept')}
          </span>
          <span className={`text-xs mobile:text-xs tablet:text-xs laptop:text-xs desktop:text-xs font-semibold px-2 mobile:px-2 tablet:px-2 laptop:px-2 desktop:px-2 py-1 mobile:py-1 tablet:py-1 laptop:py-1 desktop:py-1 rounded-full ${getScoreColor(category.avgSustainability)}`}>
            {category.avgSustainability}/100
          </span>
        </div>
        
        <Link 
          to={`/categories/${category.id}`}
          className="block w-full bg-gradient-to-r from-secondary-green to-secondary-green/90 hover:from-secondary-green/90 hover:to-secondary-green text-white font-semibold py-2 mobile:py-2 tablet:py-2 laptop:py-2 desktop:py-2 px-3 mobile:px-4 tablet:px-4 laptop:px-4 desktop:px-4 rounded-lg mobile:rounded-lg tablet:rounded-lg laptop:rounded-lg desktop:rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-secondary-green focus:ring-offset-2 transform hover:scale-[1.02] active:scale-[0.98] text-xs mobile:text-sm tablet:text-sm laptop:text-sm desktop:text-sm text-center"
        >
          {t('Visa recept')}
        </Link>
      </div>
    </div>
  );
};

export default CategoryCard;
